import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import { DownstreamConfirmDialog } from '@/components/corrections/DownstreamConfirmDialog';
import {
  useCorrectStockMovement,
  usePreviewStockMovementCorrection,
  type DownstreamPreview,
  type StockMovementCorrectionInput,
} from '@/features/corrections/hooks';
import {
  ADJUSTMENT_REASONS,
  ADJUSTMENT_REASON_LABELS,
  STOCK_MOVEMENT_TYPE_LABELS,
  useLocations,
  type AdjustmentReason,
  type StockMovementRow,
  type StockMovementType,
} from '@/features/inventory/hooks';
import { formatError } from '@/lib/errors';
import { formatDateTime } from '@/lib/formatters';

const NONE = '__none';

const SOURCE_COPY: Record<string, string> = {
  goods_receipt:
    'This came from a goods receipt. Saving updates that receipt and the purchase order, and on-hand follows.',
  return: 'This came from a customer return. Saving updates the RMA and on-hand.',
  production_form:
    'This came from a production form. Reversing finished goods also puts the consumed components back. Changing only the quantity leaves those components consumed.',
  work_order:
    'This came from a work order. On-hand is corrected, and the work order line is updated when it can be matched.',
};

function needsFrom(type: StockMovementType, fromId: string | null): boolean {
  return (
    type === 'issue' ||
    type === 'transfer' ||
    type === 'production_consume' ||
    (type === 'adjustment' && !!fromId) ||
    (type === 'cycle_count' && !!fromId)
  );
}

function needsTo(type: StockMovementType, toId: string | null): boolean {
  return (
    type === 'receipt' ||
    type === 'transfer' ||
    type === 'production_produce' ||
    type === 'adjustment' ||
    type === 'cycle_count' ||
    !!toId
  );
}

interface EditStockMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  movement: StockMovementRow | null;
}

/**
 * Correct one stock-movement row. The original stays in the ledger as
 * Reversed and a new movement carries the correction, so on-hand stays
 * tied to the audit trail. Rows that came from a receipt, return, or
 * production form update that document too.
 */
export function EditStockMovementDialog({
  open,
  onOpenChange,
  movement,
}: EditStockMovementDialogProps) {
  const preview = usePreviewStockMovementCorrection();
  const correct = useCorrectStockMovement();
  const { data: locations } = useLocations({ includeInactive: true });

  const [itemId, setItemId] = useState('');
  const [changingItem, setChangingItem] = useState(false);
  const [quantity, setQuantity] = useState('');
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [memo, setMemo] = useState('');
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPreview, setPendingPreview] = useState<DownstreamPreview | null>(null);
  const [pendingInput, setPendingInput] = useState<StockMovementCorrectionInput | null>(null);

  useEffect(() => {
    if (!open || !movement) return;
    setItemId(movement.item_id);
    setChangingItem(false);
    setQuantity(String(movement.quantity));
    setFromId(movement.from_location_id ?? '');
    setToId(movement.to_location_id ?? '');
    setMemo(movement.memo ?? '');
    setReason(movement.reason_code ?? '');
    setConfirmOpen(false);
    setPendingPreview(null);
    setPendingInput(null);
  }, [open, movement]);

  const locked = movement?.status === 'reversed' || !!movement?.reverses_movement_id;
  const showFrom = movement ? needsFrom(movement.type, movement.from_location_id) : false;
  const showTo = movement
    ? needsTo(movement.type, movement.to_location_id) &&
      movement.type !== 'issue' &&
      movement.type !== 'production_consume'
    : false;
  const showReason = movement
    ? movement.type === 'adjustment' || movement.type === 'cycle_count' || !!movement.reason_code
    : false;

  const locationRows = useMemo(
    () => (locations ?? []).filter((l) => l.is_active || l.id === fromId || l.id === toId),
    [locations, fromId, toId],
  );

  const buildInput = (reverse = false): StockMovementCorrectionInput | null => {
    if (!movement) return null;
    const qty = Number(quantity);
    if (!reverse && (!Number.isInteger(qty) || qty <= 0)) {
      toast.error('Quantity must be a whole number above zero. Reverse the movement to remove it.');
      return null;
    }
    if (!reverse && showFrom && !fromId) {
      toast.error('Pick the source location.');
      return null;
    }
    if (
      !reverse &&
      showTo &&
      !toId &&
      movement.type !== 'adjustment' &&
      movement.type !== 'cycle_count'
    ) {
      toast.error('Pick the destination location.');
      return null;
    }
    if (!reverse && !fromId && !toId) {
      toast.error('Pick at least one location.');
      return null;
    }
    return {
      id: movement.id,
      quantity: reverse ? movement.quantity : qty,
      item_id: itemId,
      from_location_id: showFrom ? fromId || null : null,
      to_location_id: showTo ? toId || null : null,
      memo: memo.trim() || null,
      reason_code: reason || null,
      reverse,
    };
  };

  const run = async (input: StockMovementCorrectionInput) => {
    try {
      const result = await preview.mutateAsync(input);
      if (result.severity === 'none') {
        await correct.mutateAsync(input);
        toast.success('Movement updated');
        onOpenChange(false);
        return;
      }
      setPendingInput(input);
      setPendingPreview(result);
      setConfirmOpen(true);
    } catch (err) {
      toast.error(formatError(err, 'Could not prepare that change.'));
    }
  };

  const confirmApply = async () => {
    if (!pendingInput) return;
    try {
      await correct.mutateAsync(pendingInput);
      toast.success(pendingInput.reverse ? 'Movement reversed' : 'Movement corrected');
      setConfirmOpen(false);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err, 'Could not apply that change.'));
    }
  };

  const busy = preview.isPending || correct.isPending;
  const source = movement?.reference_type ? SOURCE_COPY[movement.reference_type] : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit movement</DialogTitle>
          </DialogHeader>

          {movement && (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">
                  {STOCK_MOVEMENT_TYPE_LABELS[movement.type] ?? movement.type}
                </Badge>
                {movement.status === 'reversed' && <Badge variant="muted">Reversed</Badge>}
                {movement.reverses_movement_id && <Badge variant="outline">Reversal</Badge>}
                <span className="text-sm text-muted-foreground">
                  {formatDateTime(movement.created_at)}
                </span>
              </div>

              {source && !locked && (
                <p className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">
                  {source}
                </p>
              )}

              {locked ? (
                <p className="text-sm">
                  <span className="font-medium">{movement.items?.name ?? 'Item'}</span>
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    {movement.items?.sku}
                  </span>
                  <span className="mt-1 block tabular-nums">
                    {Number(movement.quantity).toLocaleString()} {movement.items?.base_uom}
                  </span>
                </p>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>Item</Label>
                      <button
                        type="button"
                        className="text-xs font-medium text-muted-foreground hover:text-foreground"
                        onClick={() => setChangingItem((v) => !v)}
                      >
                        {changingItem ? 'Keep current item' : 'Change item'}
                      </button>
                    </div>
                    {changingItem ? (
                      <ItemPicker
                        label=""
                        value={itemId}
                        onChange={(item) => {
                          if (!item) return;
                          setItemId(item.id);
                          setChangingItem(false);
                        }}
                      />
                    ) : (
                      <p className="text-sm">
                        <span className="font-mono text-xs text-muted-foreground">
                          {movement.items?.sku}
                        </span>{' '}
                        {movement.items?.name}
                        {itemId !== movement.item_id && (
                          <span className="ml-2 text-xs text-muted-foreground">changed</span>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="sm-edit-qty">Quantity</Label>
                      <Input
                        id="sm-edit-qty"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value.replace(/[^\d]/g, ''))}
                        inputMode="numeric"
                        className="tabular-nums"
                      />
                    </div>
                    {showFrom && (
                      <div className="space-y-1.5">
                        <Label htmlFor="sm-edit-from">From</Label>
                        <Select
                          value={fromId || NONE}
                          onValueChange={(v) => setFromId(v === NONE ? '' : v)}
                        >
                          <SelectTrigger id="sm-edit-from">
                            <SelectValue placeholder="Source location" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Not set</SelectItem>
                            {locationRows.map((l) => (
                              <SelectItem key={l.id} value={l.id}>
                                {l.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {showTo && (
                      <div className="space-y-1.5">
                        <Label htmlFor="sm-edit-to">To</Label>
                        <Select
                          value={toId || NONE}
                          onValueChange={(v) => setToId(v === NONE ? '' : v)}
                        >
                          <SelectTrigger id="sm-edit-to">
                            <SelectValue placeholder="Destination" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Not set</SelectItem>
                            {locationRows.map((l) => (
                              <SelectItem key={l.id} value={l.id}>
                                {l.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {showReason && (
                      <div className="space-y-1.5">
                        <Label htmlFor="sm-edit-reason">Reason</Label>
                        <Select
                          value={reason || NONE}
                          onValueChange={(v) => setReason(v === NONE ? '' : v)}
                        >
                          <SelectTrigger id="sm-edit-reason">
                            <SelectValue placeholder="Optional" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>None</SelectItem>
                            {ADJUSTMENT_REASONS.map((r) => (
                              <SelectItem key={r} value={r}>
                                {ADJUSTMENT_REASON_LABELS[r]}
                              </SelectItem>
                            ))}
                            {reason && !ADJUSTMENT_REASONS.includes(reason as AdjustmentReason) && (
                              <SelectItem value={reason}>
                                {reason === 'reversal' ? 'Reversal' : reason}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="sm-edit-memo">Memo</Label>
                    <Textarea
                      id="sm-edit-memo"
                      value={memo}
                      onChange={(e) => setMemo(e.target.value)}
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="mt-6 sm:justify-between">
            {!locked ? (
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  const input = buildInput(true);
                  if (input) void run(input);
                }}
              >
                Reverse movement
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Close
              </Button>
              {!locked && (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const input = buildInput(false);
                    if (input) void run(input);
                  }}
                >
                  {busy ? 'Saving…' : 'Save changes'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DownstreamConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        preview={pendingPreview}
        confirming={correct.isPending}
        onConfirm={() => void confirmApply()}
      />
    </>
  );
}
